import { useState, useEffect, useRef } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import { useOutletContext } from "react-router-dom";

import { Box, Button, Card, styled, Typography, useTheme } from "@mui/material";
import toast from "react-hot-toast";
import { Close } from "@mui/icons-material";
import { AccountModal, Block, GCardModal, NumberCircle } from "./accountPieces";
import { AccountWrap, GradientTop, ModalBox, ModalButtonWrap, SecondaryButton } from "./components/pieces";
import { AccountHeader, CapHeader } from "./components/Typography";
import useMuiTable from "./components/useMuiTable";
import LITableHead from "./components/LITableHead";
import PayoutTableRow from "./PayoutComponents/PayoutTableRow";
import AppPagination from "./components/AppPagination";
import { Axios, buttonGradient, formatMoney, logger } from "../../common/utils";
import { baseApi } from "../../common/constants";
import PayoutTableRowMobile from "./PayoutComponents/PayoutTableRowMobile";
import LoadingSpinner from "../subComps/loadingSpinner";
import type { Size, Payout, GCardPayout, AppContext, Config, HeadCell, PayoutResponse } from "../../types";
import { LiLink } from "../../common/commonComps";

const brandNames: Record<string, string> = {
    paypal: `PayPal`,
    venmo: `Venmo`,
};

const GCColumn = styled(Box)<{ size?: Size; forceMaxWidth?: boolean }>(({ size, forceMaxWidth }) => ({
    verticalAlign: `top`,
    width: size !== `large` || forceMaxWidth ? `100%` : `49%`,
}));

const TextRow = styled(Box)({
    marginBottom: `50px`,
    height: `30px`,
    display: `flex`,
    alignItems: `center`,
    fontWeight: 700,
    color: `#333`,
});

const headCells: HeadCell[] = [
    {
        id: `Payout Date`,
        numeric: false,
        disablePadding: false,
        align: `left`,
        label: `Payout Date`,
        sx: { maxWidth: 80, padding: `15px 0 15px 28px` },
    },
    {
        id: `Payout Type`,
        numeric: false,
        disablePadding: false,
        align: `left`,
        label: `Description`,
        colSpan: 2,
    },
    {
        id: `Payout Amount`,
        numeric: false,
        disablePadding: false,
        align: `right`,
        label: `Amount`,
    },
    {
        id: `Payout Status`,
        numeric: false,
        disablePadding: false,
        align: `center`,
        label: `Status`,
        sx: { "& span": { marginRight: `8px` } },
    },
];

const payoutMap = {
    "Payout Date": (ob: Record<string, any>, context: Record<string, any>) =>
        new Date(ob[`Payout Date`] || ob.awardSelectedTs || context.nextFifteen),
    "Payout Type": (ob: Record<string, any>) =>
        brandNames[ob[`Payout Type`]] || ob[`Payout Type`] || `${ob.brandName} Gift Card`,
    "Payout Amount": (ob: Record<string, any>) => ob[`Payout Amount`] || ob.awardAmount,
    "Payout Status": (ob: Record<string, any>) => ob[`Payout Status`],
};

const getPayoutComparator = (order: "asc" | "desc", orderBy: keyof typeof payoutMap, context: Record<string, any>) => {
    return (a: Payout, b: Payout) => {
        const result =
            payoutMap[orderBy](a, context) > payoutMap[orderBy](b, context)
                ? 1
                : payoutMap[orderBy](a, context) < payoutMap[orderBy](b, context)
                  ? -1
                  : 0;
        return order === `asc` ? result : result * -1;
    };
};

const Heading = styled(Box)(() => ({
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 16,
}));

interface PayoutsProps {
    useMobileTables: boolean;
    editable: boolean;
    context: AppContext;
    setContext: (nv: Partial<Config>) => void;
    refreshContext: () => Promise<void>;
}

const Payouts = ({ useMobileTables, editable, context, setContext, refreshContext }: PayoutsProps) => {
    const [payouts, setPayouts] = useState<(Payout | GCardPayout)[]>([]);
    const [GCard, openGCard] = useState<Payout | GCardPayout | false>(false);
    const [gCardURL, setGCardURL] = useState<string | null>(null);
    const [awardInfo, setAwardInfo] = useState<Record<string, string> | null>(null);

    const { page, order, orderBy, setPage, rowsPerPage, handleChangePage, handleRequestSort, handleChangeRowsPerPage } =
        useMuiTable({
            defaultOrder: `desc`,
            defaultOrderBy: `Payout Date`,
        });
    const filteredPayouts = payouts
        .slice()
        .sort(getPayoutComparator(order, orderBy as keyof typeof payoutMap, context));
    const theme = useTheme();
    const [size] = useOutletContext<[Size]>();
    const [cancelPayout, setCancelPayout] = useState<Payout | null>(null);
    const [iframeHeight, setIframeHeight] = useState<number>(700);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        // Only load on this route (extra safety)
        if (window.location.pathname === `/account/payouts`) {
            const existing = document.querySelector(`link[data-print-gc="true"]`);
            if (!GCard && existing) {
                existing.remove();
            } else if (GCard) {
                const link = document.createElement(`link`);
                link.rel = `stylesheet`;
                link.href = `/printgc.css`; // from public/
                link.setAttribute(`data-print-gc`, `true`);

                document.head.appendChild(link);
            }
        }
    }, [GCard]);

    useEffect(() => {
        const resizeObserverRef: { current: ResizeObserver | null } = { current: null };

        const handleMessage = (event: MessageEvent) => {
            // Log every single message regardless of source so we can see what's arriving
            logger(`[tango] message received`, {
                origin: event.origin,
                data: event.data,
                source: event.source,
                iframeWindow: iframeRef.current?.contentWindow ?? `(iframe not yet rendered)`,
                sourceMatchesIframe: event.source === iframeRef.current?.contentWindow,
            });

            const iframe = iframeRef.current;
            if (!iframe) {
                console.warn(`[tango] message arrived but iframeRef.current is null — iframe may not be rendered yet`);
                return;
            }

            if (event.source !== iframe.contentWindow) {
                logger(`[tango] message ignored — source does not match iframe.contentWindow`);
                return;
            }

            let data;
            try {
                data = typeof event.data === `string` ? JSON.parse(event.data) : event.data;
            } catch (e) {
                console.warn(`[tango] could not parse message data:`, event.data, e);
                return;
            }

            logger(`[tango] message from iframe, parsed data:`, data);

            const selector = data?.container;

            if (selector) {
                logger(`[tango] container selector received:`, selector);
                try {
                    const containerEl = iframe.contentDocument?.querySelector(selector);
                    if (containerEl) {
                        logger(`[tango] found container element, attaching ResizeObserver`, containerEl);
                        resizeObserverRef.current?.disconnect();
                        const observer = new ResizeObserver(([entry]) => {
                            logger(`[tango] ResizeObserver fired, new height:`, entry.contentRect.height);
                            setIframeHeight(entry.contentRect.height);
                        });
                        observer.observe(containerEl);
                        resizeObserverRef.current = observer;
                    } else {
                        console.warn(`[tango] selector "${selector}" found no element in iframe.contentDocument`);
                    }
                } catch (e) {
                    console.warn(`[tango] cross-origin block prevented contentDocument access:`, (e as Error).message);
                    if (data?.height) {
                        logger(`[tango] using height fallback from message:`, data.height);
                        setIframeHeight(Number(data.height));
                    }
                }
            } else if (data?.height) {
                logger(`[tango] direct height value from message:`, data.height);
                setIframeHeight(Number(data.height));
            } else {
                logger(`[tango] message had no container selector or height — full data above`);
            }
        };

        logger(`[tango] window "message" listener attached`);
        window.addEventListener(`message`, handleMessage);
        return () => {
            window.removeEventListener(`message`, handleMessage);
            resizeObserverRef.current?.disconnect();
            logger(`[tango] window "message" listener removed`);
        };
    }, []);

    const fetchPayouts = () =>
        Axios.get<PayoutResponse>(`${baseApi}payout`)
            .then(r => {
                const { programTransactions = [], gCards = [] } = r.data;
                const combined = [...programTransactions, ...gCards].sort((a, b) => {
                    const dateA = new Date(a[`Payout Date`] || (a as GCardPayout).awardSelectedTs || 0);
                    const dateB = new Date(b[`Payout Date`] || (b as GCardPayout).awardSelectedTs || 0);
                    return dateB.getTime() - dateA.getTime();
                });

                setPayouts(combined);
                setPage(0);
            })
            .catch(() => {
                toast.error(`Sorry something went wrong`);
            });

    const cancelFunc = (v: Payout) => {
        Axios.delete(`${baseApi}cashback?id=${v.cancelId}&offset=${new Date().getTimezoneOffset()}`)
            .then(refreshContext)
            .then(fetchPayouts)
            .then(() => setCancelPayout(null))
            .catch(() => {
                toast.error(`Sorry something went wrong`);
                setCancelPayout(null);
            });
    };

    const getCard = (v: string) =>
        Axios.get(`${baseApi}gift?h=${v}`)
            .then(r => r.data)
            .then((response: Record<string, string>) => {
                if (response.url) {
                    setGCardURL(response.url);
                }
                if (response.awardCode || response.awardURL) {
                    setAwardInfo(response);
                }
            })
            .catch(() => {
                setGCardURL(null);
                setAwardInfo(null);
                openGCard(false);
                toast.error(`Sorry something went wrong`);
            });

    const showCard = (v: Payout | GCardPayout) => {
        openGCard(v);
        getCard(v.id);
    };

    useState(() => {
        if (context?.adminConfig?.mall?.CORE_SETTINGS?.[`Payout Method`] === `Request`) {
            headCells[4] = {
                id: ``,
                numeric: false,
                disablePadding: false,
                align: `center`,
                label: ``,
            };
        }
        const gcardToOpen = sessionStorage.getItem(`op`);
        sessionStorage.removeItem(`op`);
        fetchPayouts().then(() => {
            if (gcardToOpen) {
                showCard({ id: gcardToOpen } as Payout);
            }
        });
    });

    return (
        <Block size={size}>
            {!!cancelPayout && (
                <AccountModal onClose={() => setCancelPayout(null)} size={size} open={!!cancelPayout}>
                    <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                        <Close
                            sx={{
                                position: `absolute`,
                                top: 24,
                                right: 10,
                                zIndex: 30,
                                cursor: `pointer`,
                                opacity: 0.55,
                                height: 25,
                                width: 25,
                                zindex: 30,
                            }}
                            onClick={() => setCancelPayout(null)}
                        />
                        {size !== `small` && <GradientTop />}
                        <ModalBox>
                            <Heading sx={{ marginBottom: 3 }}>Cancel Request</Heading>
                            <Typography sx={{ marginBottom: 6 }}>
                                Cancel your payout for {formatMoney(cancelPayout[`Payout Amount`])}?
                            </Typography>
                            <ModalButtonWrap>
                                <SecondaryButton
                                    onClick={() => {
                                        setCancelPayout(null);
                                    }}
                                >
                                    Keep it
                                </SecondaryButton>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => cancelFunc(cancelPayout)}
                                    sx={{
                                        background: buttonGradient(theme),
                                        padding: `4px 8px`,
                                        minWidth: `unset`,
                                        borderRadius: 2,
                                        "&.Mui-disabled": {
                                            background: `#999 !important`,
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    Cancel it
                                </Button>
                            </ModalButtonWrap>
                        </ModalBox>
                    </Card>
                </AccountModal>
            )}
            <GCardModal noMinSize={gCardURL ? false : true} onClose={() => openGCard(false)} size={size} open={!!GCard}>
                <Card sx={{ boxSizing: `border-box` }} className="modalContent">
                    <Close
                        sx={{
                            position: `absolute`,
                            top: 24,
                            right: 10,
                            zIndex: 30,
                            cursor: `pointer`,
                            opacity: 0.55,
                            height: 25,
                            width: 25,
                            zindex: 30,
                        }}
                        onClick={() => {
                            openGCard(false);
                            setGCardURL(null);
                            setAwardInfo(null);
                        }}
                    />
                    {size === `large` && <GradientTop />}
                    {!gCardURL && !awardInfo && (
                        <Box sx={{ padding: `40px` }}>
                            <LoadingSpinner size={30} />
                        </Box>
                    )}
                    {(gCardURL || awardInfo) && (
                        <ModalBox flexDirection={size !== `large` ? `column` : `row`}>
                            <GCColumn forceMaxWidth={!gCardURL} size={size}>
                                <Typography
                                    component="h1"
                                    variant="h4"
                                    sx={{
                                        fontFamily: `Plus Jakarta Sans`,
                                        width: `100%`,
                                        fontSize: size === `small` ? `28px` : `36px`,
                                        letterSpacing: `0.01em`,
                                        fontWeight: 700,
                                        whiteSpace: `balanced`,
                                        marginBottom: `50px`,
                                    }}
                                >
                                    Unlock your card and you&apos;re ready to go
                                </Typography>
                                <TextRow>
                                    <NumberCircle>
                                        <span>1</span>
                                    </NumberCircle>
                                    {gCardURL ? (
                                        <span>Click &quot;View reward credentials&quot;</span>
                                    ) : (
                                        <span>
                                            Click{" "}
                                            <LiLink target="_blank" rel="noopener noreferrer" to={awardInfo?.awardURL!}>
                                                HERE
                                            </LiLink>{" "}
                                            to access your reward.
                                        </span>
                                    )}
                                </TextRow>
                                {(gCardURL || awardInfo?.awardCode) && (
                                    <TextRow>
                                        <NumberCircle>
                                            <span>2</span>
                                        </NumberCircle>
                                        {gCardURL ? `Click` : `Use the code: ${awardInfo?.awardCode} to `}{" "}
                                        &quot;Unlock&quot; the gift card
                                    </TextRow>
                                )}
                            </GCColumn>
                            {gCardURL && gCardURL !== null && (
                                <GCColumn id="print-root" sx={{ marginBottom: `30px` }} size={size}>
                                    <iframe
                                        ref={iframeRef}
                                        id="tango-iframe"
                                        style={{
                                            width: `100%`,
                                            height: iframeHeight,
                                            marginBottom: `30px`,
                                            paddingRight: `1px`,
                                            border: `1px solid #DFDFDF`,
                                            overflow: `hidden`,
                                            display: `block`,
                                        }}
                                        title="gcard"
                                        src={gCardURL}
                                    />
                                </GCColumn>
                            )}
                        </ModalBox>
                    )}
                </Card>
            </GCardModal>
            <AccountWrap size={size}>
                <AccountHeader size={size}>Payout History</AccountHeader>
                <Card
                    sx={{
                        borderRadius: `12px 12px 0 0`,
                        px: 3,
                        background: theme.palette.account.background,
                    }}
                >
                    <CapHeader size={size} py={3} m={0}>
                        PAYOUTS
                    </CapHeader>
                </Card>
                <Card
                    sx={{ borderRadius: `0 0 12px 12px`, px: 3, pb: 3, background: theme.palette.account.background }}
                >
                    <TableContainer sx={{ borderRadius: `16px`, border: `1px solid #E1E1E1`, overflow: `hidden` }}>
                        <Table
                            sx={{
                                border: `none`,
                            }}
                        >
                            <LITableHead
                                rowCount={filteredPayouts.length}
                                onRequestSort={handleRequestSort}
                                {...{ useMobileTables, editable, setContext, order, orderBy, headCells, context }}
                            />
                            <TableBody sx={{ background: `#FFF` }}>
                                {filteredPayouts
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(payout =>
                                        useMobileTables ? (
                                            <PayoutTableRowMobile
                                                key={payout.id}
                                                onClick={(p: Payout) => setCancelPayout(p)}
                                                payout={payout as Payout}
                                                context={context}
                                                openGCard={showCard}
                                            />
                                        ) : (
                                            <PayoutTableRow
                                                key={payout.id}
                                                onClick={(p: Payout) => setCancelPayout(p)}
                                                payout={payout as Payout}
                                                context={context}
                                                openGCard={showCard}
                                            />
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
                <AppPagination
                    page={page}
                    component="div"
                    rowsPerPage={rowsPerPage}
                    count={filteredPayouts.length}
                    onPageChange={handleChangePage}
                    rowsPerPageOptions={[25, 50, 100]}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
                />
            </AccountWrap>
        </Block>
    );
};

export default Payouts;
